// A DM an agent held, across a reload (Fab 2026-10-21: "decide where it goes BEFORE showing it").
// The daemon's queue frame says what it still holds, but it lands after the DM history. So a DM I
// sent that was held when the tab closed stays hidden (in the bar) only until the first queue frame
// for its agent, or HIDE_MAX_MS, whichever comes first: never longer. Listed: the frame keeps it in
// the bar; not listed, or timed out: it is shown, and forgotten.
const KEY = "cyborg7-held-dms";
const CAP = 100;
export const HIDE_MAX_MS = 5_000;
// Past the daemon's longest hold (60 min): an id that old cannot still be held.
const EXPIRE_MS = 61 * 60_000;

type Held = Record<string, { agentId: string; at: number }>; // client message id -> holder

function read(): Held {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const fresh: Held = {};
    for (const [id, v] of Object.entries(all)) {
      const e = v as { agentId?: unknown; at?: unknown };
      if (
        typeof e?.agentId === "string" &&
        typeof e.at === "number" &&
        Date.now() - e.at < EXPIRE_MS
      )
        fresh[id] = { agentId: e.agentId, at: e.at };
    }
    return fresh;
  } catch {
    return {}; // intentional: an unreadable entry only means nothing is hidden on this load
  }
}

function write(held: Held): void {
  try {
    const keys = Object.keys(held);
    const kept =
      keys.length > CAP ? Object.fromEntries(keys.slice(-CAP).map((k) => [k, held[k]])) : held;
    globalThis.localStorage?.setItem(KEY, JSON.stringify(kept));
  } catch {
    // intentional: storage full or blocked; a reload then shows the row until the frame (as before)
  }
}

class HeldDmReload {
  // Held when the page loaded, not yet answered by a frame (or the timeout).
  pending = $state<ReadonlySet<string>>(new Set());
  private loaded: Held = {};
  private timer: ReturnType<typeof setTimeout> | undefined;

  // Call once per load (and per workspace switch).
  start(): void {
    this.loaded = read();
    write(this.loaded); // expired ids leave the store
    this.pending = new Set(Object.keys(this.loaded));
    clearTimeout(this.timer);
    if (this.pending.size === 0) return;
    this.timer = setTimeout(() => {
      // No frame answered them in time: shown, and never hidden again on a later load.
      for (const id of this.pending) this.forget(id);
      this.pending = new Set();
    }, HIDE_MAX_MS);
  }

  // The agent holding a pending one (its bar shows it until the frame answers).
  agentFor(clientMsgId: string): string | undefined {
    return this.pending.has(clientMsgId) ? this.loaded[clientMsgId]?.agentId : undefined;
  }

  remember(clientMsgId: string, agentId: string): void {
    write({ ...read(), [clientMsgId]: { agentId, at: Date.now() } });
  }

  forget(clientMsgId: string): void {
    const held = read();
    if (!(clientMsgId in held)) return;
    delete held[clientMsgId];
    write(held);
  }

  // The agent's queue frame: its ids are answered (listed ones stay held by the frame itself).
  frame(agentId: string, listed: ReadonlySet<string>): void {
    const held = read();
    let changed = false;
    for (const [id, owner] of Object.entries(held)) {
      if (owner.agentId !== agentId || listed.has(id)) continue;
      delete held[id];
      changed = true;
    }
    if (changed) write(held);
    const answered = [...this.pending].filter((id) => this.loaded[id]?.agentId === agentId);
    if (answered.length > 0)
      this.pending = new Set([...this.pending].filter((id) => !answered.includes(id)));
  }
}

export const heldDmReload = new HeldDmReload();
